/**
 * @fileoverview useDeviceManagement Hook - Manages user's encryption devices
 *
 * This hook provides state management for viewing and managing the user's
 * WebAuthn devices (primary and linked). Includes device listing, promotion,
 * and revocation.
 *
 * **Usage:**
 * ```tsx
 * const { devices, primaryDevices, revokeDevice, promoteToPrimary } = useDeviceManagement();
 *
 * // List all devices
 * devices.forEach(device => logger.info(device.deviceName));
 *
 * // Revoke a device
 * await revokeDevice(deviceId);
 * ```
 *
 * @module hooks/useDeviceManagement
 */

import { useState, useEffect, useCallback } from 'react';
import { createLogger } from '@xanectech/lib';
import type { Device } from '@xanectech/types';
import {
  listUserDevices,
  listPrimaryDevices as fetchPrimaryDevices,
  revokeDevice as revokeDeviceService,
  promoteToPrimary as promoteDeviceService,
} from '../services/DeviceManagementService';
import { useAuth } from '../contexts/AuthContext';

const logger = createLogger('useDeviceManagement');

export function useDeviceManagement() {
  const { user } = useAuth();
  const [devices, setDevices] = useState<Device[]>([]);
  const [primaryDevices, setPrimaryDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Load devices from database
   */
  const loadDevices = useCallback(async () => {
    if (!user) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {

      // Load all devices
      const allDevices = await listUserDevices(user.id);
      setDevices(allDevices);

      // Load primary devices
      const primaries = await fetchPrimaryDevices(user.id);
      setPrimaryDevices(primaries);

      logger.info('Devices loaded successfully', {
        total: allDevices.length,
        primary: primaries.length,
      });
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : 'Failed to load devices';
      logger.error('Failed to load devices', err as Error);
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  /**
   * Revoke a device
   *
   * @param deviceId - Device ID to revoke
   */
  const revokeDevice = useCallback(
    async (deviceId: string): Promise<boolean> => {
      if (!user) {
        logger.error('No user authenticated');
        return false;
      }

      try {
        logger.info('Revoking device', { deviceId });
        await revokeDeviceService(deviceId);

        // Reload devices
        await loadDevices();

        logger.info('Device revoked successfully', { deviceId });
        return true;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to revoke device';
        logger.error('Failed to revoke device', err as Error);
        setError(errorMsg);
        return false;
      }
    },
    [user, loadDevices],
  );

  /**
   * Promote a linked device to primary
   *
   * @param deviceId - Linked device ID to promote
   * @param credentialId - WebAuthn credential ID for the device
   */
  const promoteToPrimary = useCallback(
    async (deviceId: string, credentialId: string): Promise<boolean> => {
      if (!user) {
        logger.error('No user authenticated');
        return false;
      }

      try {
        logger.info('Promoting device to primary', { deviceId });
        await promoteDeviceService(deviceId, credentialId);

        // Reload devices
        await loadDevices();

        logger.info('Device promoted successfully', { deviceId });
        return true;
      } catch (err) {
        const errorMsg =
          err instanceof Error ? err.message : 'Failed to promote device';
        logger.error('Failed to promote device', err as Error);
        setError(errorMsg);
        return false;
      }
    },
    [user, loadDevices],
  );

  /**
   * Get device by ID
   *
   * @param deviceId - Device ID
   * @returns Device or undefined
   */
  const getDevice = useCallback(
    (deviceId: string): Device | undefined => {
      return devices.find((d) => d.id === deviceId);
    },
    [devices],
  );

  /**
   * Check if user has any primary devices
   */
  const hasPrimaryDevice = useCallback((): boolean => {
    return primaryDevices.length > 0;
  }, [primaryDevices]);

  /**
   * Get active devices count
   */
  const getActiveDevicesCount = useCallback((): number => {
    return devices.filter((d) => d.status === 'active').length;
  }, [devices]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Load devices on mount and when user changes
  useEffect(() => {
    void loadDevices();
  }, [loadDevices]);

  return {
    devices,
    primaryDevices,
    isLoading,
    error,
    loadDevices,
    revokeDevice,
    promoteToPrimary,
    getDevice,
    hasPrimaryDevice,
    getActiveDevicesCount,
    clearError,
  };
}
